import { createSlice, createSelector } from '@reduxjs/toolkit';

/**
 * CMS Slice - Manages CMS layout data from DatoCMS
 *
 *
 * This slice stores:
 * - productLayouts: Map of productId -> structure data (lightweight, cached)
 * - tabContents: Map of cacheKey -> tab content (on-demand, cached)
 * - tabContentLoading: Map of cacheKey -> loading state
 * - loading: Whether a structure fetch is in progress
 * - error: Any error that occurred during fetch
 * - currentProductId: The product ID currently being viewed
 * - currentModelId: For products with models (e.g., Guided Selling)
 * - currentTabSlug: Currently selected tab slug
 */

const initialState = {
  // Phase 1: Product structure cache (lightweight)
  productLayouts: {},
  loading: false,
  error: null,
  currentProductId: null,
  currentModelId: null,

  // Phase 2: Tab content cache (on-demand)
  // Key format: "productId-tabSlug" or "productId-modelId-tabSlug"
  tabContents: {},
  tabContentLoading: {},
  tabContentErrors: {},
  currentTabSlug: null,
};

const cmsSlice = createSlice({
  name: 'cms',
  initialState,
  reducers: {
    /**
     * Start fetching CMS layout for a product
     * @param {Object} action.payload - { productId }
     */
    fetchLayoutStart: (state, action) => {
      state.loading = true;
      state.error = null;
      state.currentProductId = action.payload.productId;
    },

    /**
     * Successfully fetched CMS layout
     * @param {Object} action.payload - { productId, layoutData }
     */
    fetchLayoutSuccess: (state, action) => {
      const { productId, layoutData } = action.payload;
      state.loading = false;
      state.error = null;
      state.currentProductId = productId;

      // Cache the layout data by productId
      // Store null if no layout found (to avoid re-fetching)
      state.productLayouts[productId] = {
        data: layoutData,
        fetchedAt: Date.now(),
      };
    },

    /**
     * Failed to fetch CMS layout
     * @param {Object} action.payload - { productId, error }
     */
    fetchLayoutFailure: (state, action) => {
      const { productId, error } = action.payload;
      state.loading = false;
      state.error = error;

      // Cache the failure to avoid re-fetching
      state.productLayouts[productId] = {
        data: null,
        fetchedAt: Date.now(),
        error: error,
      };
    },

    /**
     * Clear all CMS layout data
     */
    clearLayout: (state) => {
      state.productLayouts = {};
      state.loading = false;
      state.error = null;
      state.currentProductId = null;
    },

    /**
     * Clear layout for a specific product
     * @param {Object} action.payload - { productId }
     */
    clearProductLayout: (state, action) => {
      const { productId } = action.payload;
      delete state.productLayouts[productId];
    },

    /**
     * Set current product ID (without fetching)
     * @param {Object} action.payload - { productId }
     */
    setCurrentProductId: (state, action) => {
      state.currentProductId = action.payload.productId;
    },

    /**
     * Set current model ID (for products with models)
     * @param {Object} action.payload - { modelId }
     */
    setCurrentModelId: (state, action) => {
      state.currentModelId = action.payload.modelId;
    },

    // =========================================================================
    // Phase 2: Tab Content Actions (Lazy Loading)
    // =========================================================================

    /**
     * Set current tab slug
     * @param {Object} action.payload - { tabSlug }
     */
    setCurrentTabSlug: (state, action) => {
      state.currentTabSlug = action.payload.tabSlug;
    },

    /**
     * Start fetching tab content
     * @param {Object} action.payload - { cacheKey }
     */
    fetchTabContentStart: (state, action) => {
      const { cacheKey } = action.payload;
      state.tabContentLoading[cacheKey] = true;
      delete state.tabContentErrors[cacheKey];
    },

    /**
     * Successfully fetched tab content
     * @param {Object} action.payload - { cacheKey, content }
     */
    fetchTabContentSuccess: (state, action) => {
      const { cacheKey, content } = action.payload;
      state.tabContents[cacheKey] = {
        data: content,
        fetchedAt: Date.now(),
      };
      state.tabContentLoading[cacheKey] = false;
    },

    /**
     * Failed to fetch tab content
     * @param {Object} action.payload - { cacheKey, error }
     */
    fetchTabContentFailure: (state, action) => {
      const { cacheKey, error } = action.payload;
      state.tabContentLoading[cacheKey] = false;
      state.tabContentErrors[cacheKey] = error;
    },

    /**
     * Clear tab content cache for a specific tab
     * @param {Object} action.payload - { cacheKey }
     */
    clearTabContent: (state, action) => {
      const { cacheKey } = action.payload;
      delete state.tabContents[cacheKey];
      delete state.tabContentLoading[cacheKey];
      delete state.tabContentErrors[cacheKey];
    },

    /**
     * Clear all tab content cache
     */
    clearAllTabContents: (state) => {
      state.tabContents = {};
      state.tabContentLoading = {};
      state.tabContentErrors = {};
      state.currentTabSlug = null;
    },
  },
});

// Action creators
export const {
  // Phase 1: Structure actions
  fetchLayoutStart,
  fetchLayoutSuccess,
  fetchLayoutFailure,
  clearLayout,
  clearProductLayout,
  setCurrentProductId,
  setCurrentModelId,
  // Phase 2: Tab content actions
  setCurrentTabSlug,
  fetchTabContentStart,
  fetchTabContentSuccess,
  fetchTabContentFailure,
  clearTabContent,
  clearAllTabContents,
} = cmsSlice.actions;

// Selectors
export const selectCmsState = (state) => state.cms;

export const selectCmsProductLayouts = (state) =>
  state.cms?.productLayouts || {};

export const selectCmsLoading = (state) => state.cms?.loading || false;

export const selectCmsError = (state) => state.cms?.error || null;

export const selectCmsCurrentProductId = (state) =>
  state.cms?.currentProductId || null;

export const selectCmsCurrentModelId = (state) =>
  state.cms?.currentModelId || null;

/**
 * Check if a product's layout is already cached
 * @param {Object} state - Redux state
 * @param {number} productId - Product ID to check
 * @returns {boolean}
 */
export const selectIsProductCached = (state, productId) => {
  return productId in (state.cms?.productLayouts || {});
};

/**
 * Get cached layout data for a specific product
 * @param {Object} state - Redux state
 * @param {number} productId - Product ID
 * @returns {Object|null}
 */
export const selectProductLayout = (state, productId) => {
  return state.cms?.productLayouts?.[productId]?.data || null;
};

/**
 * Memoized selector to get layout data for current product
 */
export const selectCmsLayoutData = createSelector(
  [selectCmsProductLayouts, selectCmsCurrentProductId],
  (productLayouts, currentProductId) => {
    if (!currentProductId) {
      return null;
    }
    return productLayouts[currentProductId]?.data || null;
  }
);

/**
 * Memoized selector to get models for current product
 */
export const selectCmsModels = createSelector(
  [selectCmsLayoutData],
  (layoutData) => {
    if (!layoutData) {
      return null;
    }
    return layoutData?.product?.models || null;
  }
);

/**
 * Memoized selector to get goLiveSource config for current product
 * Returns the product-level config (goLiveSource and goLiveSourceRules)
 */
export const selectCmsGoLiveSourceConfig = createSelector(
  [selectCmsLayoutData],
  (layoutData) => {
    if (!layoutData?.product) {
      return null;
    }
    const { goLiveSource, goLiveSourceRules } = layoutData.product;
    if (!goLiveSource && !goLiveSourceRules) {
      return null;
    }
    return { goLiveSource, goLiveSourceRules };
  }
);

/**
 * Memoized selector to get goLiveSource config for current model
 * Used for model-based products where each model may have different routing
 */
export const selectCmsModelGoLiveSourceConfig = createSelector(
  [selectCmsLayoutData, selectCmsCurrentModelId],
  (layoutData, currentModelId) => {
    if (!layoutData?.product?.models || !currentModelId) {
      return null;
    }
    const model = layoutData.product.models.find(
      (m) => m.modelId === currentModelId || m.id === currentModelId
    );
    if (!model) {
      return null;
    }
    const { goLiveSource, goLiveSourceRules } = model;
    if (!goLiveSource && !goLiveSourceRules) {
      return null;
    }
    return { goLiveSource, goLiveSourceRules };
  }
);

/**
 * Memoized selector to get the effective goLiveSource config
 * Prefers model-level config over product-level config when available
 */
export const selectEffectiveGoLiveSourceConfig = createSelector(
  [selectCmsGoLiveSourceConfig, selectCmsModelGoLiveSourceConfig],
  (productConfig, modelConfig) => {
    // Model-level config takes precedence if available
    return modelConfig || productConfig || null;
  }
);

/**
 * Memoized selector to get tabs for current product
 * Supports both direct tabs (product.tabs) and model-based tabs (product.models[].tabs)
 */
export const selectCmsTabs = createSelector(
  [selectCmsLayoutData, selectCmsCurrentModelId],
  (layoutData, currentModelId) => {
    if (!layoutData) {
      return null;
    }

    // First check for direct tabs on product
    if (layoutData?.product?.tabs?.length > 0) {
      return layoutData.product.tabs;
    }

    // Then check for models with tabs
    const models = layoutData?.product?.models;
    if (models?.length > 0) {
      // If a specific model is selected, return its tabs
      if (currentModelId) {
        const selectedModel = models.find(
          (m) => m.modelId === currentModelId || m.id === currentModelId
        );
        if (selectedModel?.tabs?.length > 0) {
          return selectedModel.tabs;
        }
      }
      // Default to first model's tabs if no model selected
      if (models[0]?.tabs?.length > 0) {
        return models[0].tabs;
      }
    }

    return null;
  }
);

/**
 * Memoized selector to check if CMS has layout for current product/model
 * For model-based products, checks if the current model has CMS tabs configured
 */
export const selectHasCmsLayout = createSelector(
  [selectCmsLayoutData, selectCmsLoading, selectCmsCurrentModelId],
  (layoutData, loading, currentModelId) => {
    if (loading) {
      return false;
    }
    if (!layoutData) {
      return false;
    }

    // If product has direct tabs, CMS layout is available
    if (layoutData?.product?.tabs?.length > 0) {
      return true;
    }

    // For model-based products, check if the current model has tabs
    const models = layoutData?.product?.models;
    if (models?.length > 0) {
      // If a specific model is selected, check if it has tabs
      if (currentModelId) {
        const selectedModel = models.find(
          (m) => m.modelId === currentModelId || m.id === currentModelId
        );
        // Only return true if the selected model has tabs configured
        return selectedModel?.tabs?.length > 0;
      }
      // If no model selected, check if first model has tabs (fallback)
      return models[0]?.tabs?.length > 0;
    }

    return false;
  }
);

/**
 * Selector to check if a product's CMS status is known (cached)
 * Returns true if we've already checked this product (even if result was null/no CMS)
 * Returns false if we haven't checked yet (need to fetch)
 */
export const selectIsProductCmsStatusKnown = createSelector(
  [selectCmsProductLayouts, selectCmsCurrentProductId],
  (productLayouts, currentProductId) => {
    if (!currentProductId) {
      return false;
    }
    // Check if this product exists in the cache (even if data is null)
    return currentProductId in productLayouts;
  }
);

// ============================================================================
// Phase 2: Tab Content Selectors (Lazy Loading)
// ============================================================================

/**
 * Select all tab contents cache
 */
export const selectTabContents = (state) => state.cms?.tabContents || {};

/**
 * Select all tab content loading states
 */
export const selectTabContentLoading = (state) =>
  state.cms?.tabContentLoading || {};

/**
 * Select all tab content errors
 */
export const selectTabContentErrors = (state) =>
  state.cms?.tabContentErrors || {};

/**
 * Select current tab slug
 */
export const selectCurrentTabSlug = (state) =>
  state.cms?.currentTabSlug || null;

/**
 * Generate cache key for tab content
 * @param {number} productId - Product ID
 * @param {number|null} modelId - Model ID (optional)
 * @param {string} tabSlug - Tab slug
 * @returns {string} Cache key
 */
export const getTabCacheKey = (productId, modelId, tabSlug) => {
  if (!productId || !tabSlug) {
    return '';
  }
  return modelId
    ? `${productId}-${modelId}-${tabSlug}`
    : `${productId}-${tabSlug}`;
};

/**
 * Select tab content by cache key
 * @param {Object} state - Redux state
 * @param {string} cacheKey - Cache key
 * @returns {Array|null} Tab content or null
 */
export const selectTabContentByCacheKey = (state, cacheKey) => {
  if (!cacheKey) {
    return null;
  }
  return state.cms?.tabContents?.[cacheKey]?.data || null;
};

/**
 * Select tab content loading state by cache key
 * @param {Object} state - Redux state
 * @param {string} cacheKey - Cache key
 * @returns {boolean}
 */
export const selectTabContentLoadingByCacheKey = (state, cacheKey) => {
  if (!cacheKey) {
    return false;
  }
  return state.cms?.tabContentLoading?.[cacheKey] || false;
};

/**
 * Select tab content error by cache key
 * @param {Object} state - Redux state
 * @param {string} cacheKey - Cache key
 * @returns {string|null}
 */
export const selectTabContentErrorByCacheKey = (state, cacheKey) => {
  if (!cacheKey) {
    return null;
  }
  return state.cms?.tabContentErrors?.[cacheKey] || null;
};

/**
 * Memoized selector to get current tab content
 * Uses currentProductId, currentModelId, and currentTabSlug to determine cache key
 */
export const selectCurrentTabContent = createSelector(
  [
    selectTabContents,
    selectCmsCurrentProductId,
    selectCmsCurrentModelId,
    selectCurrentTabSlug,
  ],
  (tabContents, productId, modelId, tabSlug) => {
    if (!productId || !tabSlug) {
      return null;
    }
    const cacheKey = getTabCacheKey(productId, modelId, tabSlug);
    return tabContents[cacheKey]?.data || null;
  }
);

/**
 * Memoized selector to check if current tab content is loading
 */
export const selectIsCurrentTabContentLoading = createSelector(
  [
    selectTabContentLoading,
    selectCmsCurrentProductId,
    selectCmsCurrentModelId,
    selectCurrentTabSlug,
  ],
  (loadingStates, productId, modelId, tabSlug) => {
    if (!productId || !tabSlug) {
      return false;
    }
    const cacheKey = getTabCacheKey(productId, modelId, tabSlug);
    return loadingStates[cacheKey] || false;
  }
);

/**
 * Memoized selector to get current tab content error
 */
export const selectCurrentTabContentError = createSelector(
  [
    selectTabContentErrors,
    selectCmsCurrentProductId,
    selectCmsCurrentModelId,
    selectCurrentTabSlug,
  ],
  (errors, productId, modelId, tabSlug) => {
    if (!productId || !tabSlug) {
      return null;
    }
    const cacheKey = getTabCacheKey(productId, modelId, tabSlug);
    return errors[cacheKey] || null;
  }
);

export default cmsSlice.reducer;
